import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FileText,
  BookOpen,
  Layout,
  CheckSquare,
  Video,
  Download,
  Search,
  TrendingDown,
} from "lucide-react";
import { useState, useMemo, JSX } from "react";
import { resources } from "@/data";
import { cn } from "@/lib/utils";
import { addNotification } from "@/store";

export const Route = createFileRoute("/resources")({
  component: ResourcesPage,
});

const typeIconMap: Record<string, React.ElementType> = {
  pdf: FileText,
  guide: BookOpen,
  template: Layout,
  checklist: CheckSquare,
  video: Video,
};

const categories = [
  "All",
  "Compliance",
  "eTIMS",
  "Corporate Tax",
  "PAYE & Payroll",
  "International Tax",
  "Updates",
];

// eslint-disable-next-line react-refresh/only-export-components
function ResourcesPage(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    return resources.filter((r) => {
      const matchesCat =
        activeCategory === "All" || r.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleDownload = (title: string) => {
    addNotification("success", `Downloading "${title}"...`);
  };

  return (
    <>
      <Helmet>
        <title>Tax Resources | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Free Kenyan tax resources: filing calendars, eTIMS guides, PAYE templates, and more. Download expert resources from Rosewood Tax Consultancy."
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Free Tax <span className=" text-[#6B1E2A]">Resources</span>
            </h1>
            <p className="text-lg text-[#6B6B6B]">
              Download guides, templates, checklists and calendars to keep your
              business tax-compliant in Kenya.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured resources */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A] mb-6">
            Most Downloaded
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {resources
              .filter((r) => r.featured)
              .map((resource, i) => {
                const Icon = typeIconMap[resource.type] || FileText;
                return (
                  <motion.div
                    key={resource.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4 p-5 bg-[#FAF7F2] rounded-xl hover:shadow-xs transition-all"
                  >
                    <div className="w-12 h-12 bg-[#6B1E2A] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1A1A1A] text-sm mb-1 line-clamp-1">
                        {resource.title}
                      </p>
                      <p className="text-xs text-[#6B6B6B] mb-2 line-clamp-2">
                        {resource.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-[10px] text-[#6B6B6B]">
                          <TrendingDown size={10} />
                          {resource.downloads.toLocaleString()} downloads
                        </div>
                        <button
                          onClick={() => handleDownload(resource.title)}
                          className="flex items-center gap-1 text-xs font-semibold text-[#6B1E2A] hover:text-[#4A1219] transition-colors"
                        >
                          <Download size={12} />
                          {resource.fileSize}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </div>
      </section>

      {/* All resources */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Search + filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-11 pr-4 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                    activeCategory === cat
                      ? "bg-[#6B1E2A] text-white"
                      : "bg-[#FAF7F2] text-[#6B6B6B]",
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {filtered.map((resource, i) => {
              const Icon = typeIconMap[resource.type] || FileText;
              return (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-5 p-5 bg-[#FAF7F2] rounded-xl hover:shadow-xs transition-all group"
                >
                  <div className="w-12 h-12 bg-[#6B1E2A]/10 group-hover:bg-[#6B1E2A] rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <Icon
                      size={20}
                      className="text-[#6B1E2A] group-hover:text-white transition-colors"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#1A1A1A] text-sm">
                        {resource.title}
                      </h3>
                      <span className="px-2 py-0.5 bg-[#6B1E2A]/8 text-[#6B1E2A] text-[10px] font-semibold rounded uppercase">
                        {resource.type}
                      </span>
                      <span className="px-2 py-0.5 bg-[#FAF0E8] text-[#A07832] text-[10px] font-medium rounded">
                        {resource.category}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B6B6B] line-clamp-1">
                      {resource.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="hidden md:flex flex-col items-end">
                      <span className="text-xs font-medium text-[#1A1A1A]">
                        {resource.fileSize}
                      </span>
                      <span className="text-[11px] text-[#6B6B6B]">
                        {resource.downloads.toLocaleString()} downloads
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(resource.title)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#6B1E2A] text-white text-xs font-semibold rounded-lg hover:bg-[#4A1219] transition-colors"
                    >
                      <Download size={13} />
                      Download
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#6B6B6B]">
                No resources found matching your search.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
